import React from 'react';
import { ProgressbarCircle, AlertFixed, Button } from '../../ui';
import { DEFAULT_IMAGE_SIZE } from '../../pages/album-settings/image-compress-range';
import { imageReduced, getVideoSnapshot } from './utils';
import { userSettingsServices } from '../../store/services/settings.service';
import {AddBoxOutlined} from '@mui/icons-material';

const ALERT_MESSAGES = {
    TOO_MANY_MEMORY: 'File troppo grandi selezionati, il limite è di ',
    ERROR_DURING_UPLOAD: 'Errore durante il caricamento dei file, potrebbe essere causato dal caricamento di file non salvati in locale. Salvali in locale e riprova.'
}

const CONVERT_VALUE = 1000000;

export function UploadButtonAlbum({ active, memoryAvailable, handleNewMedia, albumId, isOnTop }) {
    const [showAlert, setShowAlert] = React.useState(false);
    const [internalImageUploading, setInternalImageUploading] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState('');
    const [uploadProgress, setUploadProgress] = React.useState(0);
    const allImagesRef = React.useRef(0);
    const imagesUploadedRef = React.useRef(0);
    const stopUploadRef = React.useRef(false);
    const newMediaRef = React.useRef([]);
    const allFileSizeRef = React.useRef(0);
    const imagesIndexUploaded = React.useRef([]);

    const stopUploading = () => {
        stopUploadRef.current = true;
        setInternalImageUploading(false);
        setUploadProgress(0);
    }

    const [imageCustomSize, setImageCustomSize] = React.useState(DEFAULT_IMAGE_SIZE);

    React.useEffect(() => {
        userSettingsServices.get().then((res) => {
            if (res?.settings?.imageCustomSize) {
                setImageCustomSize(parseFloat(res.settings.imageCustomSize));
            }
        });
    }, []);

    const uploadFileWithProgress = async (files, index) => {
        if (stopUploadRef.current || index >= files.length) {
            return;
        }
        const file = files[index];
        let fileReduced = file.type.includes('image') ? await imageReduced(file) : await getVideoSnapshot(file);
        if (file.type.includes('video')) {
            fileReduced = await imageReduced(fileReduced);
        }
        const fileCustomReduce = await imageReduced(file, imageCustomSize);

        const xhr = new XMLHttpRequest();
        const formattedData = new FormData();
        formattedData.append('file', fileCustomReduce);
        formattedData.append('album_id', albumId);
        if (fileReduced) {
            formattedData.append('file_reduced', fileReduced);
        }

        const url = `${process.env.REACT_APP_API_URL}file`;
        xhr.open('POST', url, true);

        // Aggiungi il token come intestazione Authorization
        const token = localStorage.getItem('token');
        if (token) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                const progress = Math.round((event.loaded / event.total) * 100);
                const totalProgress = Math.round(((imagesUploadedRef.current + progress / 100) / allImagesRef.current) * 100);
                setUploadProgress(totalProgress);
                if (progress === 100) {
                    if (!imagesIndexUploaded.current.includes(index)) {
                        imagesIndexUploaded.current.push(index);
                        imagesUploadedRef.current += 1;
                        uploadFileWithProgress(files, index + 1);
                    }
                }
            }
        };

        xhr.onload = () => {
            if (xhr.status === 201) {
                const fileUploaded = JSON.parse(xhr.responseText)
                newMediaRef.current.push(fileUploaded);
                if (imagesUploadedRef.current === allImagesRef.current) {
                    handleNewMedia(newMediaRef.current, allFileSizeRef.current / CONVERT_VALUE);
                    stopUploading();
                }
            } else {
                stopUploading();
            }
        };

        xhr.onerror = () => {
            stopUploading();

        };

        xhr.send(formattedData);
    };

    const handleButton = async (e) => {
        setInternalImageUploading(true);
        const files = e.target.files;
        const copyFiles = [...files];
        const allFileSize = copyFiles
            .filter((file) => file.type.includes('image') || file.type.includes('video'))
            .reduce((acc, file) => acc + file.size, 0);

        imagesUploadedRef.current = 0;
        stopUploadRef.current = false;
        allImagesRef.current = copyFiles.length;
        newMediaRef.current = [];
        allFileSizeRef.current = allFileSize;

        if (allFileSize > memoryAvailable) {
            setAlertMessage('Memoria insufficiente per caricare tutti i file selezionati');
            setShowAlert(true);
            setInternalImageUploading(false);
            return;
        }

        // check biggest file size is lower than 100Mb
        const biggestFile = copyFiles.reduce((acc, file) => (file.size > acc ? file.size : acc), 0);
        if (biggestFile > 100 * CONVERT_VALUE) {
            setAlertMessage('Errore, al momento non è possibile caricare file più grandi di 100Mb. Seleziona file più piccoli e riprova.');
            setShowAlert(true);
            setInternalImageUploading(false);
            return;
        }

        e.target.value = ""; // Pulisci l'input
        imagesIndexUploaded.current = [];

        setTimeout(() => {
            uploadFileWithProgress(copyFiles, 0);
        }, 100);
    };

    const handleCloseAlert = () => {
        setShowAlert(false);
    };

    return (
        <React.Fragment>
            {isOnTop
                ? <label className="position-relative ms-1">
                    <button className={"btn h-100 ms-2 p-1 btn-outline-primary border border-3 border-primary text-dark pe-none"}>
                        <AddBoxOutlined />
                    </button>
                    <input type="file" multiple={true} onChange={handleButton} accept="image/*, video/*" lang="it" disabled={!active} 
                     className="opacity-0 w-100 h-100 position-absolute top-0 start-0" />
                </label>
                : <label className="w-100 mt-3">
                    <p className={`btn fw-bold btn-outline-primary border border-3 border-primary w-100 text-dark rounded-5 no-hover ${active ? '' : 'disabled'} d-flex justify-content-center align-items-center`}>
                        <AddBoxOutlined className="me-2" />
                        Carica Immagini o Video
                    </p>
                    <input type="file" multiple={true} className="opacity-0" onChange={handleButton} accept="image/*, video/*" lang="it" disabled={!active} />
                </label>}
            <AlertFixed message={alertMessage} showAlert={showAlert} handleCloseAlert={handleCloseAlert} />
            {internalImageUploading && (
                <div className="position-fixed top-0 start-0 w-100 h-100 bg-white d-flex justify-content-center align-items-center flex-column position-relative" style={{ zIndex: 9999 }}>
                    <ProgressbarCircle percentage={uploadProgress} delay={false} />
                    <h2 className="mt-4">{imagesUploadedRef.current} / {allImagesRef.current}</h2>
                    <h1 className="mt-4">Caricamento file...</h1>
                </div>
            )}
        </React.Fragment>
    );
}